<script setup>
import { ref, watch, nextTick, onMounted, onBeforeUnmount } from 'vue';
import gsap from 'gsap';

const props = defineProps({
    label: {
        type: String,
        default: 'Filtros',
    },
    icon: {
        type: String,
        default: 'pi pi-sliders-h',
    },
    // Nome do símbolo do Material Symbols Rounded; quando informado, substitui o 'icon'
    iconeMaterial: {
        type: String,
        default: null,
    },
    tooltip: {
        type: String,
        default: null,
    },
    ativo: {
        type: Boolean,
        default: false,
    },
    quantidade: {
        type: Number,
        default: 0,
    },
    // Mantém a label sempre visível, sem depender do hover/foco para expandir
    labelFixo: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(['click']);

const labelRef = ref(null);
const indicadorRef = ref(null);
const aberto = ref(false);
let animacao = null;

// Sem ambiente de browser (SSR, pré-render, testes sem DOM) não há matchMedia para consultar
const temBrowser = typeof window !== 'undefined' && typeof window.matchMedia === 'function';

const semAnimacao = () =>
    temBrowser ? window.matchMedia('(prefers-reduced-motion: reduce)').matches : true;

// Em telas pequenas ou sem mouse o botão fica apenas com o ícone, sem a expansão da label
const consultaDesktop = temBrowser
    ? window.matchMedia('(hover: hover) and (min-width: 768px)')
    : { matches: false, addEventListener: () => { }, removeEventListener: () => { } };

onMounted(() => {
    if (props.ativo && indicadorRef.value) {
        gsap.set(indicadorRef.value, { scale: 1, opacity: 1 });
    }

    consultaDesktop.addEventListener('change', aoMudarViewport);
});

// Mede a largura real do texto sem depender do 'auto' do gsap, que arredonda e corta a label
const medirLargura = () => {
    const el = labelRef.value;
    const larguraAnterior = el.style.width;

    el.style.width = 'auto';
    const largura = el.getBoundingClientRect().width;
    el.style.width = larguraAnterior;

    return largura;
};

const MARGEM_LABEL_REM = 0.6;

// Volta ao estado fechado sem transição, para quando a viewport deixa de ser desktop no meio do hover
const fecharImediato = () => {
    animacao?.kill();
    aberto.value = false;

    if (labelRef.value) {
        gsap.set(labelRef.value, { width: 0, marginLeft: 0, opacity: 0, x: -7 });
    }
};

// Com a label fixa o gsap não pode deixar estilos inline, senão eles vencem o CSS da classe
const restaurarLabelFixa = () => {
    animacao?.kill();
    aberto.value = false;

    if (labelRef.value) {
        gsap.set(labelRef.value, { clearProps: 'all' });
    }
};

// A label fixa continua visível em qualquer viewport; as demais voltam ao estado fechado
const aoMudarViewport = () => {
    if (props.labelFixo) restaurarLabelFixa();
    else fecharImediato();
};

watch(() => props.labelFixo, (fixo) => {
    if (fixo) restaurarLabelFixa();
    else fecharImediato();
});

// Só o ícone fica visível; a label é revelada na animação de hover/foco
const animar = (abrir) => {
    if (!labelRef.value || props.labelFixo || !consultaDesktop.matches) return;

    animacao?.kill();
    aberto.value = abrir;

    const duracao = semAnimacao() ? 0 : 1;
    const el = labelRef.value;

    animacao = gsap.timeline({ defaults: { overwrite: 'auto' } });

    if (abrir) {
        const largura = medirLargura();

        animacao
            .to(el, {
                width: largura,
                marginLeft: `${MARGEM_LABEL_REM}rem`,
                duration: 0.32 * duracao,
                ease: 'power3.out',
            })
            .to(el, {
                opacity: 1,
                x: 0,
                duration: 0.26 * duracao,
                ease: 'power2.out',
            }, 0.08 * duracao);

        return;
    }

    animacao
        .to(el, {
            opacity: 0,
            x: -7,
            duration: 0.14 * duracao,
            ease: 'power1.in',
        })
        .to(el, {
            width: 0,
            marginLeft: 0,
            duration: 0.26 * duracao,
            ease: 'power3.inOut',
        }, 0.06 * duracao);
};

// Trocar a label com o botão expandido deixaria a largura animada com a medida do texto antigo
watch(() => props.label, async () => {
    if (props.labelFixo || !aberto.value) return;

    await nextTick();
    animar(true);
});

// O indicador entra com um leve overshoot para chamar atenção sem depender de cor forte
watch(() => props.ativo, (ativo) => {
    if (!indicadorRef.value) return;

    gsap.to(indicadorRef.value, {
        scale: ativo ? 1 : 0,
        opacity: ativo ? 1 : 0,
        duration: semAnimacao() ? 0 : 0.3,
        ease: ativo ? 'back.out(2.4)' : 'power2.in',
    });
});

onBeforeUnmount(() => {
    consultaDesktop.removeEventListener('change', aoMudarViewport);
    animacao?.kill();
    gsap.killTweensOf(indicadorRef.value);
});
</script>

<template>
    <div class="botao-filtro-espaco">
        <Button size="small" severity="secondary" outlined :aria-label="label" v-tooltip.bottom="tooltip"
            class="botao-filtro" :class="{ 'botao-filtro--ativo': ativo }" @click="emit('click', $event)"
            @mouseenter="animar(true)" @mouseleave="animar(false)" @focus="animar(true)" @blur="animar(false)">
            <span v-if="iconeMaterial" class="material-symbols-rounded notranslate botao-filtro__icone"
                :class="{ 'botao-filtro__icone--ativo': ativo }">{{ iconeMaterial }}</span>
            <span v-else :class="[icon, 'text-xs']"></span>
            <span ref="labelRef" class="text-xs botao-filtro__label"
                :class="{ 'botao-filtro__label--fixo': labelFixo }">{{ label }}</span>
        </Button>

        <span ref="indicadorRef" class="botao-filtro__indicador"
            :class="{ 'botao-filtro__indicador--contador': quantidade > 0 }" aria-hidden="true">
            {{ quantidade > 0 ? quantidade : '' }}
        </span>
    </div>
</template>

<style scoped>
.botao-filtro-espaco {
    position: relative;
    flex: 0 0 auto;
}

/* Estilo de segmented control: inativo é o "trilho" cinza, ativo é a pastilha branca */
.botao-filtro-espaco .p-button.botao-filtro {
    justify-content: center;
    white-space: nowrap;
    /* Mesma borda do campo de busca (.p-inputtext) para alinhar o cabeçalho */
    border: 1px solid #d1d5db;
    border-radius: 6px;
    color: var(--surface-500, #71717a);
    font-weight: 500;
    box-shadow: none;
    transition: background-color 0.2s, color 0.2s, border-color 0.2s;
}

.botao-filtro-espaco .p-button.botao-filtro:enabled:hover {
    border-color: #9f9f9f;
    background: var(--surface-200, #e4e4e7);
    color: var(--surface-700, #3f3f46);
}

.botao-filtro-espaco .p-button.botao-filtro:enabled:focus {
    border-color: #dedede;
    box-shadow: 0 0 0 0.2rem #d8d8d83b;
}

.botao-filtro-espaco .p-button.botao-filtro--ativo {
    border-color: #d1d5db;
    /* background: var(--surface-0, #ffffff); */
    color: var(--surface-900, #18181b);
    font-weight: 500;
    /* box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.08); */
}

.botao-filtro-espaco .p-button.botao-filtro--ativo:enabled:hover {
    border-color: #9f9f9f;
    background: #efefef;
    color: var(--surface-900, #18181b);
    /* box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.1); */
}

/* Material Symbols nasce com 24px; reduz para acompanhar a label em text-xs */
.botao-filtro-espaco .botao-filtro__icone {
    font-size: 1rem;
    line-height: 1;
    font-variation-settings: 'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' 20;
    transition: font-variation-settings 0.2s;
}

/* Preenchido quando ligado, para o estado ser legível sem depender só da cor */
.botao-filtro-espaco .botao-filtro__icone--ativo {
    font-variation-settings: 'FILL' 1, 'wght' 400, 'GRAD' 0, 'opsz' 20;
}

.botao-filtro-espaco .botao-filtro__label {
    display: inline-block;
    flex: 0 0 auto;
    width: 0;
    opacity: 0;
    overflow: hidden;
    white-space: nowrap;
    transition: none;
    transform: translateX(-7px);
}

.botao-filtro-espaco .botao-filtro__label--fixo {
    width: auto;
    margin-left: 0.6rem;
    opacity: 1;
    transform: none;
}

.botao-filtro-espaco .botao-filtro__indicador {
    position: absolute;
    top: -3px;
    right: -3px;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 7px;
    height: 7px;
    border-radius: 999px;
    background: var(--surface-900, #171717);
    opacity: 0;
    transform: scale(0);
    pointer-events: none;
}

.botao-filtro-espaco .botao-filtro__indicador--contador {
    top: -6px;
    right: -6px;
    width: auto;
    min-width: 1rem;
    height: 1rem;
    padding: 0 0.25rem;
    color: var(--surface-0, #ffffff);
    font-size: 0.65rem;
    font-weight: 600;
    line-height: 1;
    border: 2px solid var(--surface-0, #ffffff);
    box-sizing: content-box;
}
</style>
